import { useMemo } from "react";
import { BidderOnboardingFormPayload } from "../../../../../components/bidder/Tabs/onboarding/BidderOnboardingFormV2";
import { useGetProfileDetails } from "../../../../../hoc";
import {
  SectionConfig,
  useProfileCompleteness,
} from "./useProfileCompleteness";
import { useSelector } from "react-redux";

type LabeledValue = {
  label: string;
  value: string;
};

type ComplexValue = string | LabeledValue | LabeledValue[];

type BetaFormObject = Record<string, ComplexValue>;

type SerializedObject = Record<string, string | string[]>;

function normalizeBetaFormObject(obj: BetaFormObject): SerializedObject {
  const result: SerializedObject = {};

  for (const key in obj) {
    const val = obj[key];

    if (typeof val === "string") {
      result[key] = val;
    } else if (Array.isArray(val)) {
      result[key] = val.map((item) => item?.value ?? "");
    } else if (val && typeof val === "object") {
      result[key] = val.value ?? "";
    } else {
      result[key] = "";
    }
  }

  return result;
}

// TODO: Legal Entity Name should be considered in percentage weightage in Type & PAN Section for non individual - other 3 options except individual "type"
export function useProfileCompletionCardControl(
  {
    excludeProfilePicture,
    formValues,
  }: {
    excludeProfilePicture?: boolean;
    formValues?: BidderOnboardingFormPayload;
  } = { excludeProfilePicture: false },
) {
  const userData = useGetProfileDetails();
  const onboardingData = useSelector((s: any) => s.bidderAuth.onboarding);
  const source = useMemo(() => {
    const liveFormValues = formValues ?? {};

    return {
      userData,
      onboardingData: {
        ...onboardingData,
        ...normalizeBetaFormObject(liveFormValues),
      },
    };
  }, [formValues, onboardingData, userData]);

  const sections: SectionConfig<typeof source>[] = useMemo(
    () => [
      {
        key: "onboarding-type-pan",
        label: "Type & PAN",
        weight: excludeProfilePicture ? 25 : 20,
        fields: [
          {
            key: "bidderType",
            resolver: (s) => s?.onboardingData?.type,
          },
          {
            key: "pan",
            resolver: (s) => s?.onboardingData?.pan,
          },
        ],
      },
      {
        key: "onboarding-basic-details",
        label: "Basic Details",
        weight: source?.onboardingData?.type?.toLowerCase()?.trim() ? 35 : 25,
        fields: [
          {
            key: "name",
            resolver: (s) => s.onboardingData?.name || s?.userData?.name,
          },
          {
            key: "email",
            resolver: (s) => s.onboardingData?.email || s?.userData?.email,
          },
          {
            key: "mobile",
            resolver: (s) => s.onboardingData?.mobile || s?.userData?.mobile,
          },
          {
            key: "alternateMobile",
            resolver: (s) => s?.onboardingData?.alternate_mobile,
          },
          ...(source?.onboardingData?.type?.toLowerCase()?.trim() ===
          "individual"
            ? [
                {
                  key: "aadhaar",
                  resolver: (s: any) => s?.onboardingData?.aadhaar,
                },
              ]
            : [
                {
                  key: "gst",
                  resolver: (s: any) =>
                    s?.onboardingData?.gst ||
                    s?.onboardingData?.legal_entity_name,
                },
              ]),
        ],
      },

      {
        key: "onboarding-location-details",
        label: "Location Details",
        weight: 20,
        fields: [
          {
            key: "pincode",
            resolver: (s) => s?.onboardingData?.pin,
          },
          {
            key: "city",
            resolver: (s) => s?.onboardingData?.city,
          },
          {
            key: "state",
            resolver: (s) => s?.onboardingData?.state,
          },
          {
            key: "country",
            resolver: (s) => s?.onboardingData?.country,
          },

          {
            key: "address",
            resolver: (s) => s?.onboardingData?.full_address,
          },
        ],
      },

      {
        key: "onboarding-preference-details",
        label: "Preference Details",
        weight: 20,
        fields: [
          {
            key: "propertyType",
            resolver: (s) =>
              s?.onboardingData?.preferred_property_type?.length > 0,
          },
          {
            key: "priceRange",
            resolver: (s) => s?.onboardingData?.price_range,
          },
          {
            key: "preferredState",
            resolver: (s) => s?.onboardingData?.preferred_state?.length > 0,
          },
          {
            key: "preferredCity",
            resolver: (s) => s?.onboardingData?.preferred_location?.length > 0,
          },
        ],
      },
      ...(excludeProfilePicture
        ? []
        : [
            {
              key: "onboarding-extra-details",
              label: "Extra",
              weight: 5,
              fields: [
                {
                  key: "profilePhoto",
                  resolver: (s: any) => s?.userData?.picture,
                },
              ],
            },
          ]),
    ],
    [excludeProfilePicture, source?.onboardingData?.type],
  );

  const { percentage, firstInCompleteSection } = useProfileCompleteness(
    source,
    sections,
  );

  const isComplete = percentage === 100;

  return {
    isComplete,
    percentage,
    firstInCompleteSection,
  };
}
